import {
  type DropdownMenuCheckboxItemProps,
  type DropdownMenuContentProps,
  type DropdownMenuItemProps,
} from '@radix-ui/react-dropdown-menu';
import {type TooltipContentProps} from '@radix-ui/react-tooltip';
import {IconButton} from '@wandb/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon, type IconName} from '@wandb/weave/components/Icon';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import React, {useRef, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';

const DEFAULT_CONTENT_PROPS: DropdownMenuContentProps = {};
const DEFAULT_TOOLTIP_PROPS: TooltipContentProps = {};

type OverflowMenuProps = {
  'aria-label': string;
  children: React.ReactNode;
  contentProps?: DropdownMenuContentProps;
  'data-dd-action-name'?: string;
  'data-test'?: string;
  isDisabled?: boolean;
  isRootModal?: boolean;
  tooltip?: string;
  variant?: 'ghost' | 'secondary';
};

export const OverflowMenu = ({
  'aria-label': ariaLabel,
  children,
  contentProps = DEFAULT_CONTENT_PROPS,
  'data-dd-action-name': ddActionName,
  'data-test': dataTest,
  isDisabled,
  isRootModal,
  tooltip,
  variant,
}: OverflowMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const annoyinglySpreadPropsToTrigger = {ref: triggerRef} as any;
  return (
    <Tooltip.Provider>
      <DropdownMenu.Root
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        modal={isRootModal}>
        <DropdownMenu.Trigger
          data-test={dataTest}
          asChild
          {...annoyinglySpreadPropsToTrigger}>
          <IconButton
            icon="overflow-horizontal"
            variant={variant || 'ghost'}
            onPress={e => e.continuePropagation()}
            isActive={isMenuOpen}
            isDisabled={isDisabled}
            aria-label={ariaLabel}
            data-dd-action-name={ddActionName}
            // A registered action name means the element is static UI text,
            // so it's safe to unmask in session replay too.
            data-dd-privacy={ddActionName ? DD_PRIVACY_ALLOW : undefined}
            tooltip={{children: tooltip}}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            data-test="nw-overflow-menu-content"
            // z-index of 6 needed here to exceed resizeable workspace panels,
            // which go to z-5 when they are hovered.
            className="z-[6] shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]"
            align="end"
            sideOffset={12}
            onCloseAutoFocus={e => e.preventDefault()}
            onInteractOutside={e => {
              // In non-modal mode (isRootModal={false}) there is no focus
              // trap, so when react-aria's IconButton returns focus to the
              // trigger at press end, radix's dismissable layer reads that
              // as an outside interaction and closes the menu it just
              // opened. The trigger is not "outside" in intent — veto
              // dismissals that originate on it. (Consumers can still
              // override via contentProps.)
              const target = e.target as Node | null;
              if (target && triggerRef.current?.contains(target)) {
                e.preventDefault();
              }
            }}
            {...contentProps}>
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Tooltip.Provider>
  );
};

type OverflowMenuItemProps = {
  displayName: string;
  icon: IconName;
  tooltipContent?: React.ReactElement | string;
  tooltipProps?: TooltipContentProps;
} & DropdownMenuItemProps;

export const OverflowMenuItem = ({
  className,
  'data-dd-action-name': ddActionName,
  displayName,
  disabled,
  icon,
  tooltipContent,
  tooltipProps = DEFAULT_TOOLTIP_PROPS,
  ...dropdownItemProps
}: OverflowMenuItemProps & {'data-dd-action-name'?: string}) => {
  return (
    <DropdownMenu.Item
      className={twMerge(
        'radix-disabled:pointer-events-auto radix-disabled:cursor-not-allowed',
        className
      )}
      data-dd-action-name={ddActionName}
      // A registered action name means the element is static UI text,
      // so it's safe to unmask in session replay too.
      data-dd-privacy={ddActionName ? DD_PRIVACY_ALLOW : undefined}
      disabled={disabled}
      {...dropdownItemProps}>
      <div className="flex w-full items-center justify-between gap-16">
        <div className="flex items-center gap-8">
          <Icon className="shrink-0" name={icon} role="presentation" />
          <span>{displayName}</span>
        </div>
        {tooltipContent && (
          <div className="flex">
            <IconButton
              variant="ghost"
              icon="info"
              size="sm"
              aria-label="Additional options"
              tooltip={{
                children: tooltipContent,
                placement: 'right',
              }}
            />
          </div>
        )}
      </div>
    </DropdownMenu.Item>
  );
};

export const OverflowMenuCheckboxItem = ({
  displayName,
  icon,
  ...dropdownCheckboxItemProps
}: {
  displayName: string;
  icon: IconName;
} & DropdownMenuCheckboxItemProps) => {
  return (
    <DropdownMenu.CheckboxItem
      className="flex justify-between text-wb-text-primary dark:text-moon-300"
      {...dropdownCheckboxItemProps}>
      <div className="flex items-center gap-8">
        <Icon className="shrink-0" name={icon} />
        {displayName}
      </div>
      <DropdownMenu.ItemIndicator>
        <Icon name="checkmark" width={18} height={18} />
      </DropdownMenu.ItemIndicator>
    </DropdownMenu.CheckboxItem>
  );
};

/**
 * Create a wrapped OverflowMenuItem component with preconfigured props.
 * Use this to configure consistent props across all items within a menu.
 */
export const makeOverflowMenuItem =
  ({
    tooltipProps: outerTooltipProps,
    ...outerProps
  }: Partial<OverflowMenuItemProps> = {}) =>
  ({
    tooltipProps: innerTooltipProps = DEFAULT_TOOLTIP_PROPS,
    ...innerProps
  }: OverflowMenuItemProps) => {
    return (
      <OverflowMenuItem
        tooltipProps={{
          ...outerTooltipProps,
          ...innerTooltipProps,
        }}
        {...outerProps}
        {...innerProps}
      />
    );
  };
